import constants, { publicConstants } from '../constants';
import ContextProcessor from '../context/context-processor';
import { getContextValue, getDataObject } from '../store/state/state-manager';
import type {
  CaseInfo,
  CaseAction,
  JSActionQueryParams,
  SecondaryButtons,
  DisplayButtonsOnErrors,
  ChainedData
} from '../case/types';
import { C11nEnv } from '../interpreter/c11n-env';
import CaseUtils from '../case/utils';
import { EXECUTE_OBJECT_WIDE_ACTION, FINISH_ASSIGNMENT } from '../store/state/actions';
import * as StateManager from '../store/state/state-manager';
import * as actionType from '../store/state/actions';
import { getTargetFromContainerItemID } from '../container/container-utils';

import Utils from './utils';

/**
 * [isLocalAction]
 * Description : Checks if the active action is a local action or not. Returns true for a local action else false.
 * @param caseInfo        Case info object of current context in the Redux store.
 * @returns Returns true for a local action else false.
 */
const isLocalAction = (caseInfo: CaseInfo) => {
  const { activeActionID, availableActions } = caseInfo || {};
  if (activeActionID && availableActions) {
    const actionObj = availableActions.find((caseAction) => caseAction.ID === activeActionID);
    return Boolean(actionObj);
  }
  return false;
};

/**
 * Function returns true if the current assignment is in create stage else false using the caseInfo object of DX API response
 * @param caseInfo - Case info object of DX API response
 *
 * @returns - Returns true if the current assignment is in create stage else false
 */
const isAssignmentInCreateStage = (caseInfo: CaseInfo) => {
  if (CaseUtils.isObjectCaseType(String(caseInfo?.objectTypeID)) && !isLocalAction(caseInfo)) {
    return true;
  }
  if (caseInfo) {
    const { stageID, stages = [] } = caseInfo;
    const stageCount = stages.length;
    for (let index = 0; index < stageCount; index += 1) {
      const currentStage = stages[index];
      if (currentStage.ID === stageID) {
        return stages[index].transitionType === 'create' && stages[index].visited_status === 'active';
      }
    }
  }
  return false;
};

const isAssignmentInManualStage = (caseInfo: CaseInfo) => {
  if (caseInfo) {
    const { stageID, stages = [] } = caseInfo;
    const stageCount = stages.length;
    for (let index = 0; index < stageCount; index += 1) {
      const currentStage = stages[index];
      if (currentStage.ID === stageID) {
        return stages[index].transitionType === 'manual';
      }
    }
  }
  return false;
};

const isCaseResolved = (caseInfo: CaseInfo) => {
  if (caseInfo) {
    const { stageID, stages = [] } = caseInfo;
    const stageCount = stages.length;
    for (let index = 0; index < stageCount; index += 1) {
      const currentStage = stages[index];
      if (currentStage.ID === stageID) {
        return stages[index].transitionType === 'resolution';
      }
    }
  }
  return false;
};

/**
 * Check if the current assignment is assigned to passed user  and return true if it is assigned else false based in the DX API response of create work action.
 * @param currentOperatorIdentifier - Current operator id
 * @param caseInfo - Case info object of DX API response
 *
 * @returns - Returns true if the current assignment is assigned to created operator else false
 */
const didOperatorHasAssignment = (currentOperatorIdentifier?: string, caseInfo?: CaseInfo) => {
  if (currentOperatorIdentifier && caseInfo) {
    const assigneeInfo = Utils.getIn(caseInfo, ['assignments', '0', 'assigneeInfo'], {});
    const { ID, type } = assigneeInfo;
    return type?.toLowerCase() === 'worklist' && ID === currentOperatorIdentifier;
  }
  return false;
};

/**
 * Traverses through the assignments list node and returns the action ID of the assignment.
 * @param assignments All the assignments of the case<br>
 * @param assignmentID The work object's assignment ID<br>
 * For example: "ASSIGN-WORKLIST OPB1HW-SPACE-WORK RA-14011!REQUEST_FLOW_0"
 * @returns FlowAction ID of the assignment
 * For example: "EnterRequest_0"
 */
const getAssignmentActionID = (assignments: CaseAction[], assignmentID: string) => {
  if (!Array.isArray(assignments) || !assignmentID) {
    throw new Error('Invalid parameters passed to getAssignmentActionID API');
  }

  return assignments.find((assignment) => assignment.ID === assignmentID)?.actions?.[0]?.ID;
};

const getLastActiveItem = (targetContainer: { accessedOrder: []; items: [] }) => {
  const { accessedOrder, items } = targetContainer;

  if (accessedOrder && accessedOrder.length > 1) {
    const lastActiveItemKey = accessedOrder[accessedOrder.length - 2];
    const lastActiveItem = items[lastActiveItemKey];
    const { semanticURL } = lastActiveItem;

    // If semanticURL is present return it else empty string at the end of this function
    if (semanticURL) {
      return lastActiveItem;
    }
  }
  return { semanticURL: '' };
};

/**
 * [isCaseWideAction]
 * Description : Checks if the active action is a case wide action or not. Returns true for a casewide action else false.
 * @param context        Data context in the Redux store.
 * @param pageReference  Page reference in the context.
 */
const isCaseWideAction = (context: string | null, pageReference: string) => {
  const localActionID = ContextProcessor.getPropertyValue(
    publicConstants.CASE_INFO.ACTIVE_ACTION_ID,
    pageReference,
    context
  );
  const caseActions: [CaseAction] = ContextProcessor.getPropertyValue(
    publicConstants.CASE_INFO.AVAILABLEACTIONS,
    pageReference,
    context
  );
  let caseWideAction = false;
  if (caseActions && localActionID) {
    const actionObj = caseActions.find((caseAction) => caseAction.ID === localActionID);
    if (actionObj) {
      // To ignore the case in the string, check actionType by converting it to same case.
      caseWideAction = actionObj.type.toLowerCase() === publicConstants.LOCAL_ACTION_TYPE.CASE_WIDE.toLowerCase();
    }
  }
  if (context && !caseActions) {
    const data = getDataObject(context);
    caseWideAction = data?.context_data?.actionType === constants.CASE;
  }
  return caseWideAction;
};
/**
 * Description : Checks if the active action is a perform action
 * @param context Data context in the redux store
 * @returns Returns true for a perform action
 * @private
 */
const isPerform = (context: string) => {
  return (
    getContextValue(null, constants.CASE_VIEW_MODE_PROP, context) === constants.PERFORM_MODE &&
    (context.includes(constants.WORKAREA) || context.includes(constants.MODAL))
  );
};

/**
 * Description : Checks if the active action is an assignment
 * @param context Data context in redux store
 * @param pageReference Page reference in the context
 * @returns Returns true for a assignment else false.
 */
const isAssignment = (context: string, pageReference: string) => {
  return isPerform(context) && !isCaseWideAction(context, pageReference);
};

/**
 * Checks if current case is child case or not
 * @param caseInfo   Case info object
 *
 * @returns Returns true if child case else false
 */
const isChildCase = (caseInfo: CaseInfo) => {
  const parentCaseID = caseInfo?.parentCaseInfo?.ID;
  return Boolean(caseInfo && parentCaseID);
};

/**
 * Description : Returns actionID by checking assignments and assignmentId
 * @param assignments Assignments in case info
 * @param assignmentID Assignment Id in case info
 * @param localActionID Localaction Id in case info
 * @returns Returns Localaction Id in case info else will return FlowAction ID of the assignment
 */
const getActionId = (assignments: CaseAction[], assignmentID: string, localActionID: string): string | undefined => {
  let actionID;
  const actions = assignments?.find((assignment) => assignment.ID === assignmentID)?.actions;
  const assignmentAction = actions?.filter((action) => action.ID === constants.FIX_BROKEN_ASSIGNMENTS) || [];
  if (localActionID && assignmentAction?.length > 0) {
    // Broken assignment scenario during 'TransferAssignment' local action
    actionID = assignmentAction[0].ID;
  } else if (localActionID) {
    actionID = localActionID;
  } else {
    actionID = getAssignmentActionID(assignments, assignmentID);
  }
  return actionID;
};

const getActionIdForFinishAssignment = (
  assignments: CaseAction[],
  assignmentID: string,
  localActionID: string,
  activeActionID: string
): string | undefined => {
  let actionID;
  if (assignments) {
    actionID = getActionId(assignments, assignmentID, localActionID);
  } else if (activeActionID) {
    actionID = activeActionID;
  } else {
    actionID = 'Create';
  }
  return actionID;
};

const getFinishAssignmentActionType = (caseTypeID: string, isObjectWideAction: boolean) =>
  CaseUtils.isObjectCaseType(caseTypeID) || isObjectWideAction ? EXECUTE_OBJECT_WIDE_ACTION : FINISH_ASSIGNMENT;

const getContainerDataForFinishAssignment = (
  containerItemID: string,
  c11nEnv: C11nEnv,
  options: { isTriggeredFromDifferentContext?: boolean }
) => {
  let assignmentID;
  let caseID;
  let assignments;
  let localActionID;
  let caseActions;
  let caseInfo;
  let businessID;
  let caseTypeID;
  let caseTypeName;
  const containerData = getDataObject(containerItemID);
  const caseType = containerData?.caseInfo?.caseTypeID || containerData?.objectInfo?.objectTypeID;

  if (options?.isTriggeredFromDifferentContext) {
    assignmentID = containerData.caseInfo.assignments[0].ID;
    caseID = containerData.caseInfo.ID;
    assignments = containerData.caseInfo.assignments;
    localActionID = containerData.caseInfo.activeActionID;
    caseActions = containerData.caseInfo.availableActions;
    caseInfo = containerData.caseInfo;
    businessID = containerData.caseInfo.businessID;
    caseTypeID = containerData.caseInfo.caseTypeID;
    caseTypeName = containerData.caseInfo.caseTypeName;
  } else if (containerData?.objectInfo && CaseUtils.isObjectCaseType(caseType)) {
    /* If objectInfo is present in containerData, use directly from objectInfo object */
    caseID = containerData.objectInfo.ID;
    localActionID = containerData.objectInfo.activeActionID;
    caseActions = containerData.objectInfo.availableActions;
    caseInfo = containerData.objectInfo;
    businessID = containerData.objectInfo.businessID;
    caseTypeID = containerData.objectInfo.objectTypeID;
    caseTypeName = containerData.objectInfo.objectTypeName;
  } else {
    assignmentID = c11nEnv.getValue(constants.getProperty(publicConstants.CASE_INFO.ASSIGNMENT_ID));
    caseID = c11nEnv.getValue(constants.getProperty(publicConstants.CASE_INFO.CASE_INFO_ID));
    assignments = c11nEnv.getValue(constants.getProperty(publicConstants.CASE_INFO.ASSIGNMENTS));
    localActionID = c11nEnv.getValue(constants.getProperty(publicConstants.CASE_INFO.ACTIVE_ACTION_ID));
    caseActions = c11nEnv.getValue(constants.getProperty(publicConstants.CASE_INFO.AVAILABLEACTIONS));
    caseInfo = c11nEnv.getValue(constants.getProperty(publicConstants.CASE_INFO.CASE_INFO));
    businessID = c11nEnv.getValue(constants.getProperty(publicConstants.CASE_INFO.BUSINESS_ID));
    caseTypeID = c11nEnv.getValue(constants.getProperty(publicConstants.CASE_INFO.CASE_TYPE_ID));
    caseTypeName = c11nEnv.getValue(constants.getProperty(publicConstants.CASE_INFO.CASE_TYPE_NAME));
  }

  return {
    assignmentID,
    caseID,
    assignments,
    localActionID,
    caseActions,
    caseInfo,
    businessID,
    caseTypeID,
    caseTypeName
  };
};

const appendJSActionQueryParams = (jsActionQueryParams: JSActionQueryParams, restApi: string | undefined) => {
  if (Object.keys(jsActionQueryParams ?? {}).length > 0 && restApi?.startsWith('http')) {
    const url = new URL(restApi);
    const params = new URLSearchParams(url.search);

    Object.keys(jsActionQueryParams).forEach((key) => {
      params.append(key, String(jsActionQueryParams[key]));
    });

    url.search = params.toString();
    return url.toString();
  }
  return restApi;
};

// Helper function to update buttons based on conditions
const appendErrorButtonsIfAbsent = (displayButtonsOnErrors: DisplayButtonsOnErrors, buttons: SecondaryButtons) => {
  displayButtonsOnErrors.forEach((element) => {
    // Check if the element matches the button type and is an error button
    // and also ensure that the actionID is not already in buttons
    if (element.isErrorButton === true && !buttons.some((button) => button.actionID === element.actionID)) {
      buttons = [...buttons, element];
    }
  });
  return buttons;
};

const onErrorApplyErrorStateAndMergeButtons = (chainedData: ChainedData, type: string = 'case') => {
  const displayButtonsOnErrors: DisplayButtonsOnErrors =
    (chainedData?.payload?.err?.response?.data?.displayButtonsOnErrors as DisplayButtonsOnErrors) || [];

  const errorCode = chainedData?.payload?.err?.response?.status;

  const ERROR_CODE_FOR_ERROR_BUTTONS = 424;

  if (
    Array.isArray(displayButtonsOnErrors) &&
    displayButtonsOnErrors.length > 0 &&
    errorCode === ERROR_CODE_FOR_ERROR_BUTTONS
  ) {
    // Mark each button as an error button
    displayButtonsOnErrors.forEach((button) => {
      button.isErrorButton = true;
    });

    const context = chainedData.payload.context;
    const contextData = StateManager.getDataObject(context);
    const secondaryButtons = contextData?.caseInfo?.actionButtons?.secondary || [];

    // Update secondary buttons based on the provided conditions
    const updatedSecondaryButtons = appendErrorButtonsIfAbsent(displayButtonsOnErrors, secondaryButtons);
    const info = type === 'dataObject' ? publicConstants.DATA_INFO.DATA_INFO : publicConstants.CASE_INFO.CASE_INFO;

    const payload = {
      data: {
        [info]: {
          actionButtons: {
            secondary: updatedSecondaryButtons
          }
        }
      },
      target: getTargetFromContainerItemID(context),
      context
    };

    StateManager.dispatch({
      type: actionType.MERGE_DATA,
      payload
    });
  }
};

export {
  didOperatorHasAssignment,
  isAssignmentInCreateStage,
  isAssignmentInManualStage,
  getAssignmentActionID,
  getLastActiveItem,
  getActionId,
  getContainerDataForFinishAssignment,
  isCaseWideAction,
  isChildCase,
  isLocalAction,
  isPerform,
  isAssignment,
  appendJSActionQueryParams,
  getActionIdForFinishAssignment,
  getFinishAssignmentActionType,
  onErrorApplyErrorStateAndMergeButtons,
  isCaseResolved
};
